/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiArrowLeft,
  FiCalendar,
  FiShoppingBag,
  FiMonitor,
  FiCreditCard,
  FiTruck,
  FiFileText,
  FiPrinter,
  FiDollarSign,
  FiX,
  FiEdit2
} from "react-icons/fi";
import { AnimatePresence } from "framer-motion";

import PageContainer from "../../components/PageContainer";
import { useUI } from "../../theme/ThemeProvider";
import clsx from "clsx";
import Swal from "sweetalert2";
import { pickActiveDeliveryCompany } from "../../../Stores/utils/storeApi";
// أضف هذه المصفوفة في أعلى الملف لتوحيد المسميات في البرنامج كامل
const orderStatuses = [
  { id: 1, ar: "جديد", en: "New", key: "new", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { id: 2, ar: "جاري التجهيز", en: "Preparing", key: "preparing", color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  { id: 3, ar: "جاهز", en: "Ready", key: "ready", color: "bg-purple-100 text-purple-700 border-purple-200" },
  { id: 4, ar: "جاري التوصيل", en: "In Delivery", key: "delivering", color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  { id: 5, ar: "مكتمل", en: "Completed", key: "delivered", color: "bg-green-100 text-green-700 border-green-200" },
  { id: 6, ar: "مسترجع", en: "Returned", key: "returned", color: "bg-orange-100 text-orange-700 border-orange-200" },
  { id: 8, ar: "مسترجع جزئيًا", en: "Partially Returned", key: "partially_returned", color: "bg-teal-100 text-teal-700 border-teal-200" },
  { id: 7, ar: "ملغي", en: "Cancelled", key: "cancelled", color: "bg-red-100 text-red-700 border-red-200" },
  { id: 9, ar: "فشل التوصيل", en: "Delivery Failed", key: "failed", color: "bg-black text-white border-gray-800" },
];
export default function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { theme, lang, colors } = useUI();

  const isDark = theme === "dark";
  const isArabic = lang === "ar";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedStatusId, setSelectedStatusId] = useState(null);
  const [activeCustomerTab, setActiveCustomerTab] = useState("customer");
  const [deliveryInfo, setDeliveryInfo] = useState(null);

  const baseURL = (
    process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api"
  ).replace(/\/+$/, "");

  const token = localStorage.getItem("authToken");

  useEffect(() => {
    if (!orderId) return;

    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const tenantId = localStorage.getItem("tenantId");

        const res = await fetch(
          `${baseURL}/GetTenantOrders?tenantId=${tenantId}&statusId=-1`,
          {
            headers: {
              Accept: "application/json",
              Authorization: token ? `Bearer ${token}` : "",
            },
          }
        );

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (Array.isArray(data)) {
          const targetOrder = data.find(o => o.orderId === orderId);
          setOrder(targetOrder || null);
          if (targetOrder) {
            setOrder(targetOrder);
            setSelectedStatusId(Number(targetOrder.statusId || 1));

            // Fetch delivery info by cityId
            const cId = targetOrder.cityId;
            if (cId) {
              const tenantId = localStorage.getItem("tenantId");
              try {
                const delRes = await fetch(
                  `${baseURL}/TenantDelivery/GetByCity?tenantId=${tenantId}&cityId=${cId}`,
                  { headers: { Authorization: token ? `Bearer ${token}` : "" } }
                );
                if (delRes.ok) {
                  const delData = await delRes.json();
                  const activeDelivery = pickActiveDeliveryCompany(delData);
                  if (activeDelivery) {
                    setDeliveryInfo({
                      name: activeDelivery.deliveryCompanyName,
                      fee: Number(activeDelivery.deliveryCompanyFees ?? 0),
                      cityName: activeDelivery.cityName
                    });
                  }
                }
              } catch (e) {
                console.warn("Delivery fetch error:", e);
              }
            }
          }
        } else {
          setOrder(null);
        }
      } catch (err) {
        console.error("OrderDetails error:", err);
        setOrder(null);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, baseURL, token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (!order) {
    return (
      <PageContainer>
        <div className="text-center py-20">
          <p className="text-red-500 font-bold mb-4">{isArabic ? "الطلب غير موجود" : "Order not found"}</p>
          <button onClick={() => navigate(-1)} className="text-purple-600 ">
            {isArabic ? "رجوع" : "Go Back"}
          </button>
        </div>
      </PageContainer>
    );
  }

  const Card = ({ children, className }) => (
    <div className={clsx(
      "rounded-2xl border transition-all duration-300 shadow-sm",
      isDark ? "bg-[#1e293b] border-[#334155]" : "bg-white border-gray-100",
      className
    )}>
      {children}
    </div>
  );

  const StatCard = ({ icon: Icon, label, value }) => (
    <Card className="p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2 text-gray-400">
        <Icon size={18} />
        <span className="text-xs font-medium uppercase">{label}</span>
      </div>
      <div className={clsx("text-base font-bold", isDark ? "text-white" : "text-gray-900")}>
        {value}
      </div>
    </Card>
  );

  const parseOrderDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr; // Fallback to raw string if parsing fails
    return d.toLocaleString(isArabic ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // أضف هذه الدالة هنا (قبل handleSaveStatus)
  const toPascalDetails = (detailsList) => {
    if (!Array.isArray(detailsList)) return [];
    return detailsList.map(item => ({
      Id: item.id || 0,
      InvoiceNo: item.invoiceNo || "",
      ItemId: Number(item.itemId || 0),
      VariantId: Number(item.variantId || 0),
      ItemName: item.itemName || "",
      VariantName: item.variantName || "",
      ColorName: item.colorName || "",
      Size: item.size || "",
      Quantity: Number(item.quantity || 0),
      Price: Number(item.price || 0),
      ImageUrl: item.imageUrl || "",
      StockQty: Number(item.stockQty || 0),
      Discount: Number(item.discount || 0),
      LineTotal: Number(item.lineTotal || 0),
      CostPrice: Number(item.costPrice || 0)
    }));
  };
  const handleSaveStatus = async () => {
    if (!order || !selectedStatusId) return;

    try {
      setIsSaving(true);
      const tenantIdNum = Number(localStorage.getItem("tenantId"));

      // العثور على اسم الحالة بناءً على الـ ID
      const statusObject = orderStatuses.find(s => s.id === selectedStatusId);

      const payload = {
        ...order,
        statusId: selectedStatusId,
        status: statusObject ? (isArabic ? statusObject.ar : statusObject.en) : order.status,
        orderNo: Number(order.orderNo),
        tenantID: tenantIdNum,
        isPaid: order.isPaid ? 1 : 0,
        details: toPascalDetails(order.details) // الآن الدالة أصبحت معرفة ولن تعطي خطأ
      };
      delete payload.id;

      const res = await fetch(`${baseURL}/UpdateTenantOrder?tenantid=${tenantIdNum}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error("Update failed");

      setOrder(prev => ({ ...prev, statusId: selectedStatusId }));
      Swal.fire({ icon: "success", title: isArabic ? "تم التحديث" : "Updated" });
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: isArabic ? "فشل التحديث" : "Failed" });
    } finally {
      setIsSaving(false);
    }
  };

  const calculatedSubtotal = (order?.details || []).length > 0
    ? (order?.details || []).reduce((acc, item) => acc + (Number(item.lineTotal) || 0), 0)
    : Number(order?.totalAmount || order?.netAmount || 0);

  const discountAmount = Number(order.discountAmount || 0);
  const shippingCost = deliveryInfo ? deliveryInfo.fee : Math.max(0, Number(order.netAmount || 0) - (calculatedSubtotal - discountAmount));
  const grandTotal = calculatedSubtotal - discountAmount + shippingCost;

  const formatPrice = (val) => {
    return `${Number(val || 0).toLocaleString()} ${isArabic ? "د.ع" : "IQD"}`;
  };

  const handleEditOrder = () => {
    const isManual = order.isManual === 1 || order.isManual === true || order.transactionId === "manual" ? 1 : 0;
    navigate(`/admin-store/orders/create?isManual=${isManual}&id=${encodeURIComponent(order.orderId)}`, {
      state: { order }
    });
  };

  return (
    <PageContainer
      title=""
      dir={isArabic ? "rtl" : "ltr"}
    >
      <div className="max-w-6xl mx-auto space-y-6 pb-20">

        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className={clsx(
                "flex items-center justify-center w-10 h-10 rounded-full border transition-all",
                isDark ? "border-gray-700  text-gray-300" : "border-gray-200  text-gray-600"
              )}
            >
              {isArabic ? <FiArrowRight size={20} /> : <FiArrowLeft size={20} />}
            </button>
            <h1 className={clsx("text-xl md:text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
              {isArabic ? "تفاصيل الطلب" : "Order Details"} <span className="text-purple-600">#{order.orderNo}</span>
            </h1>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={handleEditOrder}
              className="flex-1 sm:flex-none px-4 py-2 rounded-xl font-bold text-sm transition-all border flex items-center justify-center gap-2 bg-amber-500 text-white border-amber-500"
            >
              <FiEdit2 size={16} />
              {isArabic ? "تعديل الطلب" : "Edit Order"}
            </button>
            <button
              onClick={handleSaveStatus}
              disabled={isSaving}
              className="flex-1 sm:flex-none px-6 py-2 rounded-xl bg-purple-600 text-white font-bold text-sm transition-all shadow-md disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : null}
              {isArabic ? "حفظ التعديلات" : "Save Changes"}
            </button>
            <button
              onClick={() => navigate(`/admin-store/orders/invoice/${orderId}`)}
              className={clsx(
                "flex-1 sm:flex-none px-4 py-2 rounded-xl font-bold text-sm transition-all border",
                isDark ? "bg-[#0f172a] border-gray-700 text-gray-300" : "bg-white border-gray-200 text-gray-600"
              )}>
              {isArabic ? "عرض الفاتورة" : "View Invoice"}
            </button>

          </div>
        </div>

        {/* Top Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            icon={FiShoppingBag}
            label={isArabic ? "رقم الطلب" : "Order No"}
            value={`#${order.orderNo}`}
          />
          <StatCard
            icon={FiCalendar}
            label={isArabic ? "تاريخ الإنشاء" : "Created Date"}
            value={parseOrderDate(order.orderDate || order.createdAt)}
          />
          <StatCard
            icon={FiMonitor}
            label={isArabic ? "مصدر الطلب" : "Order Source"}
            value={
              (order.isManual === 1 || order.isManual === true || order.transactionId === "manual")
                ? (isArabic ? "طلب يدوي" : "Manual Order")
                : (isArabic ? "المتجر الإلكتروني" : "Online Store")
            }
          />
        </div>

        {/* Status Selection */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="p-4 space-y-2">
            <div className="text-xs font-bold text-gray-400 uppercase">{isArabic ? "حالة الطلب" : "Order Status"}</div>
            <select
              value={selectedStatusId}
              onChange={(e) => setSelectedStatusId(Number(e.target.value))}
              className={clsx(
                "w-full px-4 py-2.5 rounded-xl border outline-none font-bold transition-all",
                isDark ? "bg-[#0f172a] border-gray-700 text-white focus:border-purple-500" : "bg-white border-gray-200 text-gray-900 focus:border-purple-500"
              )}
            >
              {orderStatuses.map((status) => (
                <option key={status.id} value={status.id}>
                  {isArabic ? status.ar : status.en}
                </option>
              ))}
            </select>
          </Card>
          <Card className="p-4 space-y-2">
            <div className="text-xs font-bold text-gray-400 uppercase">{isArabic ? "حالة الدفع" : "Payment Status"}</div>
            <div className={clsx(
              "w-full px-4 py-2.5 rounded-xl border font-bold",
              isDark ? "bg-[#0f172a] border-gray-700" : "bg-white border-gray-200",
              order.isPaid ? "text-green-600" : "text-red-600"
            )}>
              {order.isPaid ? (isArabic ? "مدفوع" : "Paid") : (isArabic ? "غير مدفوع" : "Unpaid")}
            </div>
          </Card>
        </div>

        {/* Products List Card */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-2">
            <h3 className="font-black text-xl flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-purple-600/10 flex items-center justify-center text-purple-600 shadow-sm border border-purple-600/20">
                <FiShoppingBag size={20} />
              </div>
              {isArabic ? "محتويات الطلب" : "Order Items"}
            </h3>
            <span className="px-4 py-1.5 rounded-full bg-purple-600 text-white text-xs font-black shadow-lg shadow-purple-600/20">
              {(order.details || []).length} {isArabic ? "منتجات" : "Items"}
            </span>
          </div>

          {(order.details || []).map((item, i) => (
            <Card key={i} className="group overflow-hidden border-none shadow-md transition-all duration-500 bg-white/50 backdrop-blur-sm">
              <div className="flex flex-col md:flex-row items-center md:items-center gap-4 md:gap-6 p-4 md:p-6">
                {/* Product Image */}
                <div className="relative shrink-0 self-center md:self-auto">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-3xl bg-white border border-gray-100 shadow-inner overflow-hidden flex items-center justify-center p-2  transition-transform duration-500">
                    {item.imageUrl || item.mainImage ? (
                      <img
                        src={(() => {
                          const src = item.imageUrl || item.mainImage;
                          if (!src) return "";
                          if (src.startsWith("http")) return src;
                          const cleanBase = baseURL.replace("/api", "");
                          const parts = src.replace(/^\/+/, "").split("/");
                          if (parts.length >= 3 && (parts[0] === "TenantDtl" || parts[0] === "Logo")) {
                            return `${cleanBase}/uploads/${parts[1]}/${parts[0]}/${parts[2]}`;
                          }
                          return `${cleanBase}${src.startsWith("/") ? "" : "/"}${src}`;
                        })()}
                        className="w-full h-full object-contain"
                        alt=""
                      />
                    ) : (
                      <FiShoppingBag size={32} className="text-gray-200" />
                    )}
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-black shadow-lg border-2 border-white">
                    {i + 1}
                  </div>
                </div>

                {/* Product Info */}
                <div className="flex-1 flex flex-col items-center md:items-start justify-center gap-1.5">
                  <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">
                    {isArabic ? "بيانات المنتج" : "Product Details"}
                  </span>
                  <div className="flex flex-col items-center md:items-start gap-2">
                    <h4 className={clsx("text-lg md:text-xl font-black transition-colors leading-tight", isDark ? "text-white" : "text-gray-900")}>
                      {item.itemName}
                    </h4>
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-purple-50 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300 text-[10px] font-black tracking-wide border border-purple-100 dark:border-purple-800">
                        {item.variantName || (isArabic ? "المنتج الأصلي" : "Standard")}
                      </span>
                      <span className="text-[10px] text-gray-400 font-mono bg-gray-50 dark:bg-gray-800 px-2 py-0.5 rounded-md border border-gray-100 dark:border-gray-700">
                        SKU: {item.itemId?.toString() || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Pricing Grid */}
                <div className="grid grid-cols-3 gap-3 md:gap-12 w-full md:w-auto pt-4 md:pt-0 border-t md:border-t-0 border-gray-100/50">
                  {/* Unit Price */}
                  <div className="flex flex-col items-center justify-center gap-1">
                    <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest text-center">{isArabic ? "سعر الوحدة" : "Unit Price"}</span>
                    <span className="font-black text-gray-700 dark:text-gray-200 text-xs md:text-base leading-none">{formatPrice(item.price)}</span>
                  </div>

                  {/* Quantity */}
                  <div className="flex flex-col items-center justify-center gap-1">
                    <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest text-center">{isArabic ? "الكمية" : "Quantity"}</span>
                    <div className="bg-gray-50 dark:bg-gray-800 px-2 md:px-3 py-1 rounded-lg md:rounded-xl text-[10px] md:text-xs font-black text-purple-600 border border-gray-100 dark:border-gray-700 shadow-sm leading-none">
                      x{item.quantity}
                    </div>
                  </div>

                  {/* Subtotal */}
                  <div className="flex flex-col items-center justify-center gap-1">
                    <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest text-center">{isArabic ? "المجموع" : "Subtotal"}</span>
                    <span className="font-black text-sm md:text-lg text-purple-600 drop-shadow-sm leading-none">{formatPrice(item.lineTotal !== undefined ? item.lineTotal : (item.price * item.quantity))}</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Invoice & Methods Summary */}
          <div className="lg:col-span-2 space-y-6">
            {/* Invoice Summary Card */}
            {/* Invoice Summary Card */}
            <Card className="p-0 overflow-hidden border-none shadow-xl">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 flex items-center justify-between text-white">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <FiFileText />
                  {isArabic ? "الفاتورة" : "Invoice"}
                </h3>
                <span className="text-xs opacity-80">{isArabic ? "ملخص مالي" : "Financial Summary"}</span>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 font-medium">{isArabic ? "قيمة المنتجات" : "Products Value"}</span>
                  <span className={clsx("font-bold text-base", isDark ? "text-white" : "text-gray-900")}>
                    {formatPrice(calculatedSubtotal)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 font-medium">{isArabic ? "الشحن" : "Shipping"}</span>
                  <span className={clsx("font-bold", shippingCost === 0 ? "text-green-500" : (isDark ? "text-white" : "text-gray-900"))}>
                    {shippingCost === 0 ? (isArabic ? "مجاني" : "Free") : formatPrice(shippingCost)}
                  </span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between items-center text-sm mt-3">
                    <span className="text-red-500 font-medium">{isArabic ? "الخصم" : "Discount"}</span>
                    <span className="font-bold text-red-500">
                      -{formatPrice(discountAmount)}
                    </span>
                  </div>
                )}
                <div className="h-px bg-gray-100 dark:bg-gray-800 my-2" />
                <div className="flex justify-between items-center text-sm">
                  <span className="font-bold text-gray-900 dark:text-white text-base">{isArabic ? "المجموع الكلي" : "Grand Total"}</span>
                  <span className="font-black text-2xl text-purple-600">
                    {formatPrice(grandTotal)}
                  </span>
                </div>

                {/* الجزء المعدل هنا */}
                <div className={clsx(
                  "mt-6 p-4 rounded-2xl border flex justify-between items-center transition-colors",
                  order.isPaid
                    ? "bg-green-50/50 dark:bg-green-900/10 border-green-100 dark:border-green-900/30"
                    : "bg-red-50/50 dark:bg-red-900/10 border-red-100 dark:border-red-900/30"
                )}>
                  <div className="flex flex-col">
                    <span className={clsx(
                      "text-[10px] uppercase font-bold tracking-wider",
                      order.isPaid ? "text-green-600 dark:text-green-500" : "text-red-600 dark:text-red-500"
                    )}>
                      {isArabic ? "حالة الدفع" : "Payment Status"}
                    </span>
                    <span className={clsx(
                      "font-black",
                      order.isPaid ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"
                    )}>
                      {order.isPaid
                        ? (isArabic ? "مدفوع بالكامل" : "Fully Paid")
                        : (isArabic ? "غير مدفوع" : "Unpaid")
                      }
                    </span>
                  </div>
                  <div className="text-end">
                    <span className="text-[10px] text-gray-400 block mb-0.5">
                      {order.isPaid
                        ? (isArabic ? "المبلغ المدفوع" : "Amount Paid")
                        : (isArabic ? "المبلغ المطلوب" : "Required Amount")
                      }
                    </span>
                    <span className={clsx(
                      "font-bold",
                      order.isPaid ? "text-green-600" : "text-red-600"
                    )}>
                      {formatPrice(grandTotal)}
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Methods Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Card className="p-0 overflow-hidden border-none shadow-lg bg-white/80 backdrop-blur-md">
                <div className="p-4 border-b border-gray-50 flex items-center gap-3 bg-gray-50/50">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600">
                    <FiCreditCard size={18} />
                  </div>
                  <h3 className="font-black text-sm text-gray-800">{isArabic ? "طريقة الدفع" : "Payment Method"}</h3>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-2xl bg-white border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center border border-purple-100 overflow-hidden text-purple-600">
                        {order.paymentMethodName?.toLowerCase().includes("cash") ? (
                          <FiDollarSign size={20} />
                        ) : (
                          <FiCreditCard size={20} />
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">{isArabic ? "الوسيلة" : "Provider"}</span>
                        <span className="font-black text-sm text-purple-600">{order.paymentMethodName || "Cash on Delivery"}</span>
                      </div>
                    </div>
                    <div className="px-2 py-1 rounded-md bg-green-50 text-green-600 text-[10px] font-black uppercase">
                      {isArabic ? "نشط" : "Active"}
                    </div>
                  </div>

                </div>
              </Card>

              <Card className="p-0 overflow-hidden border-none shadow-lg bg-white/80 backdrop-blur-md">
                <div className="p-4 border-b border-gray-50 flex items-center gap-3 bg-gray-50/50">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                    <FiTruck size={18} />
                  </div>
                  <h3 className="font-black text-sm text-gray-800">{isArabic ? "شركة التوصيل" : "Delivery Company"}</h3>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-gray-100 shadow-sm">
                    <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center text-purple-600">
                      <FiTruck size={20} />
                    </div>
                    <div className="flex flex-col flex-1">
                      <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">{isArabic ? "شركة الشحن" : "Carrier"}</span>
                      <span className="font-black text-sm text-gray-800">
                        {deliveryInfo ? deliveryInfo.name || (isArabic ? "غير محدد" : "Unknown") : (isArabic ? "لا يوجد" : "None")}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-2">
                    <span className="text-[10px] text-gray-400 font-bold">{isArabic ? "رسوم الشحن" : "Delivery Fee"}</span>
                    <span className="text-[13px] font-black text-purple-600">
                      {deliveryInfo ? formatPrice(deliveryInfo.fee) : (isArabic ? "مجاني" : "Free")}
                    </span>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Right Side Column (Customer Info & Activities) */}
          <div className="space-y-6">
            {/* Customer Info Card */}
            <Card className="overflow-hidden">
              <div className="flex border-b dark:border-gray-700">
                <button
                  onClick={() => setActiveCustomerTab("customer")}
                  className={clsx(
                    "flex-1 py-3 text-sm font-bold transition-all",
                    activeCustomerTab === "customer"
                      ? "bg-gray-50 dark:bg-gray-800 text-purple-600 border-b-2 border-purple-600"
                      : "text-gray-400"
                  )}
                >
                  {isArabic ? "العميل" : "Customer"}
                </button>
                <button
                  onClick={() => setActiveCustomerTab("receiver")}
                  className={clsx(
                    "flex-1 py-3 text-sm font-bold transition-all",
                    activeCustomerTab === "receiver"
                      ? "bg-gray-50 dark:bg-gray-800 text-purple-600 border-b-2 border-purple-600"
                      : "text-gray-400"
                  )}
                >
                  {isArabic ? "المستلم" : "Receiver"}
                </button>
              </div>
              <div className="p-6 space-y-5">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400 uppercase">{isArabic ? "الاسم" : "Name"}</span>
                  <span className="font-bold text-sm">{order.customerName}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400 uppercase">{isArabic ? "رقم الهاتف" : "Phone"}</span>
                  <span className="font-bold text-sm" dir="ltr">{order.customerPhone1}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400 uppercase">{isArabic ? "الدولة" : "Country"}</span>
                  <span className="font-bold text-sm">العراق</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400 uppercase">{isArabic ? "نوع العميل" : "Customer Type"}</span>
                  <span className="font-bold text-sm">{isArabic ? "فرد" : "Individual"}</span>
                </div>
                <button
                  onClick={() => navigate(`/admin-store/customers/details/${encodeURIComponent(order.customerName)}`)}
                  className="w-full py-2.5 rounded-xl border text-xs font-bold transition-all ">
                  {isArabic ? "عرض التفاصيل" : "View Details"}
                </button>
              </div>
            </Card>

          </div>
        </div>
      </div>
    </PageContainer>
  );
}
